// Typed DataHub client — the data plane every migrated route consumes.
// Ports the essential surface of the legacy datahub-client.js: base-URL
// resolution (edge vs local vs desktop-wrapped), the session auth token, an
// authed JSON fetch over the { ok, ... } envelope, and the data methods.
//
// Interactive login / signup / lock UI: components/AuthGate.tsx (a native Preact gate)
// wraps every page via lib/mountApp.tsx. It drives the full-page sign-in / new-user signup /
// Terms of Use / pending-approval / change-password flow through the methods below
// (login / register / getTerms / changePassword) and stores the session token under
// sessionStorage['ordoDataHubAuthToken']; token() reads it back as the Bearer on data calls.
// The app itself is not rendered until the gate confirms authentication.

import type { AssetClass } from '@ordo-mentis/ledger-store';

const STORAGE_KEY = 'ordoDataHubBaseUrl';
const AUTH_TOKEN_KEY = 'ordoDataHubAuthToken';
const LOCAL_DEFAULT = 'http://localhost:8787';
// Brand DataHub custom domain -> the ordo-datahub worker (the workers.dev URL
// still works as a fallback). The apex ordomentis.net serves the website, not the API.
const EDGE_DEFAULT = 'https://datahub.ordomentis.net';

const LOCAL_HOST_RE = /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i;

export interface HistoryResponse {
  ok?: boolean;
  ticker?: string;
  symbol?: string;
  pair?: string;
  base?: string;
  quote?: string;
  requestedSymbol?: string;
  instrumentType?: string;
  assumed?: boolean;
  contract?: string;
  history?: { closes?: number[] };
  basedOnCloses?: number;
  source?: string;
  provider?: string;
  stale?: boolean;
  note?: string;
  error?: string;
}

export function historyPathForAsset(asset: AssetClass, symbol: string, days?: number, contract?: string): string {
  const encoded = encodeURIComponent(symbol.trim().toUpperCase());
  const params = new URLSearchParams();
  if (days) params.set('days', String(days));
  if (asset === 'commodities' && contract?.trim()) params.set('contract', contract.trim().toUpperCase());
  const query = params.toString();
  const q = query ? `?${query}` : '';
  if (asset === 'crypto' || asset === 'forex' || asset === 'commodities') {
    return `/api/history/${asset}/${encoded}${q}`;
  }
  return `/api/history/${encoded}${q}`;
}

// True when a history fetch failed because the DataHub deployment lacks the
// route entirely (a stale worker's unmatched-route 404), as opposed to a real
// data/network error. The worker's unmatched-route body is
// { error: 'Unknown route.' }; a non-JSON 404 surfaces as getJson's generic
// 'DataHub 404'.
export function isHistoryRouteMissing(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err ?? '');
  return msg === 'Unknown route.' || msg === 'DataHub 404';
}

// Quiet by default, visible when provenance changes the interpretation of a
// forecast. Routine live-feed descriptions stay in diagnostics; normalization,
// assumptions, and stale-cache disclosures remain on the calculator surface.
export function historyDisclosure(response: HistoryResponse | null | undefined): string {
  if (!response || response.ok === false) return '';
  return response.requestedSymbol || response.assumed || response.stale
    ? String(response.note || '')
    : '';
}

export interface Candle {
  t: string;
  o: number;
  h: number;
  l: number;
  c: number;
  v: number;
}
export interface CandlesResponse {
  ok?: boolean;
  ticker?: string;
  timeframe?: string;
  resolution?: string;
  candles?: Candle[];
  source?: string;
  timestamp?: string;
  error?: string;
}

export interface TopAssetItem {
  symbol?: string;
  name?: string;
  price?: number;
  volume?: number;
  [extra: string]: unknown;
}

// ---- pattern engine (practice-only cockpit) ----
export interface EngineStats {
  n?: number;
  winRate?: number | null;
  expectancy?: number | null;
  profitFactor?: number | string | null;
  maxDD?: number | null;
  tStat?: number | null;
}
// Permutation ("luck") test result: does this edge beat its own ticker's
// random-timing null? pValue > gateAlpha with the gate ON blocks the queue.
export interface PermutationResult {
  enoughData?: boolean;
  pValue?: number;
  baseRateQualified?: number; // how often pure luck alone clears the same gate on this ticker
  nullPercentiles?: { p5?: number; p50?: number; p95?: number };
  k?: number;
  minK?: number;
}
// Monte Carlo forward bootstrap (1000 resampled paths of the qualified trade
// sequence): tail risk the mean expectancy hides. Historical, never a forecast.
export interface MonteCarloBand { p5?: number; p50?: number; p95?: number }
export interface EngineMonteCarlo {
  enoughData?: boolean;
  runs?: number;
  totalReturn?: MonteCarloBand;
  maxDD?: { p50?: number; p95?: number };
  probNetLoss?: number; // share of bootstrap paths that end net-negative
}
export interface EngineLeaderRow {
  ticker?: string;
  pattern?: string;
  dir?: number;
  qualified?: boolean;
  rankScore?: number | null;
  is?: EngineStats;
  oos?: EngineStats;
  regimeBreakdown?: Record<string, { n?: number; expectancy?: number }>;
  monteCarlo?: EngineMonteCarlo; // present on qualified pairs with enough trades
  permutation?: PermutationResult; // present on qualified pairs with enough trades
  qualification?: { status?: string; qualifiedStreak?: number; unqualifiedStreak?: number; runsSeen?: number; demotedAt?: string | null };
}
// Pooled cross-ticker event-study: is an event signal a tradeable edge when pooled
// across ALL names (vs per-ticker)? credible = clears the IS/OOS gate AND beats a
// pooled random-timing null AND isn't driven by one correlated time window.
export interface EngineFusionPooledRow {
  signal?: string;   // e.g. "pooled:insiderClusterBuy"
  dir?: number;
  tickers?: number;
  pooledTrades?: number;
  contributors?: Array<{ ticker: string; trades: number }>;
  qualified?: boolean;
  is?: EngineStats;
  oos?: EngineStats;
  rankScore?: number | null;
  permutation?: PermutationResult & { reason?: string };
  beatsNull?: boolean;
  credible?: boolean;
  concentration?: { windows?: number; maxWindowShare?: number | null; busiestWindow?: string | null };
  correlationFlag?: boolean;
}
export interface EngineFusionPooled extends Envelope {
  note?: string;
  study?: EngineFusionPooledRow[];
}
export interface EngineLeaderboard extends Envelope {
  generatedAt?: string;
  practice?: boolean;
  note?: string;
  entries?: EngineLeaderRow[];
  // board-level multiple-testing verdict: the best real result vs a random
  // search over the same number of tested combinations.
  permutation?: { enoughData?: boolean; pValue?: number; combosTested?: number; realBestRankScore?: number; verdict?: string };
  // the ACTUAL luck-gate state this run — so the cockpit never claims a pair
  // was "blocked" when enforcement is off or the alpha differs from a hardcode.
  permuteGate?: { enforced?: boolean; alpha?: number };
}
export interface EngineSignal {
  ticker?: string;
  pattern?: string;
  dir?: number;
  at?: string;
  close?: number;
  regime?: string | null;
  regimeAligned?: boolean | null;
  riskMult?: number;
  riskReasons?: string[];
  oosExpectancy?: number;
  oosProfitFactor?: number | string;
  oosN?: number;
  queued?: boolean;
  skipReason?: string | null;
}
export interface EngineSignals extends Envelope {
  generatedAt?: string;
  practice?: boolean;
  calendarFlags?: string[];
  signals?: EngineSignal[];
}
export interface EnginePosition {
  ticker?: string;
  pattern?: string;
  dir?: number;
  qty?: number;
  entry?: number;
  lastPrice?: number;
}
export interface EngineClosed {
  ticker?: string;
  pattern?: string;
  dir?: number;
  exit?: number;
  reason?: string;
  pnl?: number;
}
export interface EnginePortfolio extends Envelope {
  generatedAt?: string;
  practice?: boolean;
  equity?: number | null;
  cash?: number | null;
  totalReturn?: number | null;
  halted?: boolean;
  openPositions?: EnginePosition[];
  pendingOrders?: Array<{ ticker?: string; pattern?: string; dir?: number }>;
  recentClosed?: EngineClosed[];
  closedStats?: { n?: number; winRate?: number | null; profitFactor?: number | null };
}
export interface EngineRisk extends Envelope {
  generatedAt?: string;
  practice?: boolean;
  heat?: number | null;
  maxHeat?: number | null;
  clusters?: Array<{ tickers?: string[]; positions?: number; risk?: number | null }>;
}
// Real-book risk mirror: the owner's ACTUAL holdings through the same risk
// engine. The only real:true payload in the suite. Read-only, informational.
export interface EngineRealRisk extends Envelope {
  generatedAt?: string;
  practice?: false;
  real?: true;
  note?: string;
  equity?: number;
  heat?: number | null;
  maxHeat?: number | null;
  overCap?: boolean;
  hasStalePrices?: boolean; // true when any position was marked at entry (no live bars)
  clusters?: Array<{ tickers?: string[]; positions?: number; risk?: number | null; overCap?: boolean }>;
  positions?: Array<{ ticker?: string; dir?: number; qty?: number; entry?: number; stop?: number; lastPrice?: number; stopEstimated?: boolean; priceStale?: boolean; openedAt?: string }>;
}
export interface EngineRegime extends Envelope {
  generatedAt?: string;
  practice?: boolean;
  market?: { tickers?: number; trendingUp?: number; trendingDown?: number; flat?: number; highVol?: number };
  byTicker?: Record<string, string | null>;
}
export interface EngineJournal extends Envelope {
  generatedAt?: string;
  practice?: boolean;
  attribution?: {
    trades?: number;
    byPattern?: Record<string, { n?: number; winRate?: number; expectancy?: number; totalPnl?: number }>;
    byTicker?: Record<string, { n?: number; winRate?: number; expectancy?: number; totalPnl?: number }>;
    byExitReason?: Record<string, { n?: number; winRate?: number; expectancy?: number; totalPnl?: number }>;
    byRegime?: Record<string, { n?: number; winRate?: number; expectancy?: number; totalPnl?: number }>;
    decisions?: {
      taken?: number;
      skipped?: number;
      skipReasons?: Record<string, number>;
      byLayer?: Record<string, { taken?: number; skipped?: number }>;
    };
  };
  recent?: Array<Record<string, unknown>>;
}
export interface EngineCalendar extends Envelope {
  generatedAt?: string;
  practice?: boolean;
  earnings?: Array<{ ticker?: string; date?: string }> | null;
  macro?: Array<{ date?: string; label?: string }>;
  flags?: string[];
}
export interface EngineOptionsAnalytics extends Envelope {
  generatedAt?: string;
  practice?: boolean;
  note?: string;
  tickers?: Array<{
    ticker?: string;
    spot?: number;
    realizedVolAnnual?: number;
    expectedMoves?: Array<{ days?: number; oneSigma?: number; twoSigma?: number; band1?: number[]; band2?: number[] }>;
    nextEarnings?: string | null;
  }>;
}
// Combined execution-gap payload: sim-vs-paper (bridge) + sim/paper-vs-owner
// (ownerFills). Both sections are entry-focused bps; ownerFills.js is
// read-only (no network, no order path). Legacy flat aggregate/orders kept
// optional for backward tolerance.
export interface GapAggregate { n?: number; meanBps?: number; worstBps?: number }
export interface EngineExecutionGap extends Envelope {
  generatedAt?: string;
  practice?: boolean;
  note?: string;
  bridge?: { aggregate?: GapAggregate; orders?: Array<Record<string, unknown>> } | null;
  ownerFills?: {
    rows?: Array<Record<string, unknown>>;
    aggregate?: { total?: number; matched?: number; unmatched?: number; vsSim?: GapAggregate; vsPaper?: GapAggregate };
  } | null;
  aggregate?: GapAggregate; // legacy pre-tier-2 shape (tolerated)
  orders?: Array<Record<string, unknown>>;
}

// Caesar decision brain (formerly Villicus): per-signal snapshot -> actionable regime -> arbitration
// (risk override terminal) -> execution INTENT (a review draft, never an order).
export interface BrainArbitration {
  action?: 'allow' | 'observe' | 'block';
  direction?: number;
  score?: number;
  conviction?: number; // 0..1 graded conviction (directional strength x agreement - skeptic penalty)
  convictionTier?: 'high' | 'moderate' | 'low';
  concerns?: string[]; // skeptic's adversarial "why could this fail?" flags
  decidedBy?: string;
  blockedBy?: string | null;
  winningSignals?: string[];
  votes?: Array<{ name?: string; weight?: number; vote?: number; contribution?: number; note?: string; abstain?: boolean }>;
  reasons?: string[];
  riskMult?: number;
}
export interface BrainIntent {
  action?: string;
  strategy?: string;
  direction?: string;
  confidence?: number;
  entryLogic?: string;
  stopLogic?: string;
  takeProfitLogic?: string;
  invalidationLogic?: string;
  sizingLogic?: string;
  levels?: { referencePrice?: number | null; stop?: number | null; target?: number | null; rMultiple?: number | null };
  concerns?: string[];
  reasons?: string[];
}
// Governance permission ladder (judgment-of-judgment). approvedForLive is
// ALWAYS false — no score can grant live eligibility; live money stays behind
// the owner-gated bridge (passphrase + per-order 2FA).
export interface BrainGovernance {
  finalAction?: 'allow' | 'observe' | 'block';
  permissionLevel?: string; // quarantined_low_conviction | observe_low_conviction | paper_probation | paper_approved | paper_approved_high_conviction
  approvedForPaper?: boolean;
  approvedForLive?: false;
  requiresHumanReview?: boolean;
  quarantine?: boolean;
  sizeMult?: number; // conviction-scaled multiplier applied to the paper riskMult
  conviction?: number;
  convictionTier?: 'high' | 'moderate' | 'low';
  concerns?: string[];
  reasons?: string[];
}
export interface BrainDecision {
  ticker?: string;
  pattern?: string;
  snapshot?: Record<string, unknown> & { regime?: string | null; atrPercent?: number | null; volumeRatio?: number | null; realizedVolRank?: number | null };
  actionableRegime?: { regime?: string; macroCaution?: boolean; reasons?: string[] };
  arbitration?: BrainArbitration;
  governance?: BrainGovernance | null;
  intent?: BrainIntent;
}
export interface EngineBrain extends Envelope {
  generatedAt?: string;
  practice?: boolean;
  note?: string;
  decisions?: BrainDecision[];
}

// Performance Intelligence: expected (frozen backtest OOS expectancy) vs actual
// (realized paper results), joined per trade; conviction/permission buckets
// calibrate the governance ladder. Reporting only — nothing here trades.
export interface PerfOutcomeRow {
  closed?: number;
  winRate?: number | null;
  totalPnl?: number;
  expectedExp?: number | null;
  realizedExp?: number | null;
  gap?: number | null;
  drift?: 'none' | 'watch' | 'degraded' | null;
  decisions?: number;
  taken?: number;
}
export interface PerfBucketRow {
  closed?: number;
  winRate?: number;
  realizedExp?: number | null;
  totalPnl?: number;
}
export interface EnginePerformance extends Envelope {
  generatedAt?: string;
  practice?: boolean;
  note?: string;
  trades?: number;
  joinable?: number;
  expectedVsActual?: { byPair?: Record<string, PerfOutcomeRow>; byPattern?: Record<string, PerfOutcomeRow> };
  byConviction?: Record<string, PerfBucketRow>;
  byPermission?: Record<string, PerfBucketRow>;
  byLayerOutcomes?: Record<string, PerfBucketRow>;
  qualification?: {
    promoted?: Array<{ ticker?: string; pattern?: string; requalified?: boolean }>;
    demoted?: Array<{ ticker?: string; pattern?: string }>;
  };
}

// Keyless yfinance second-opinion report (GET /api/research/:ticker, local hub only).
export interface ResearchReport extends Envelope {
  ticker?: string;
  generated_at?: string;
  latest_price?: number | null;
  one_day_change_percent?: number | null;
  sma_20?: number | null;
  sma_50?: number | null;
  volatility_20_day?: number | null;
  trend?: string;
  risk_notes?: string[];
  bull_case?: string[];
  bear_case?: string[];
}

// Owner-only live-execution status (GET /api/live/status — read of state, never
// an action). ok:false when gated/off-hub; the UI hides the indicator then.
export interface LiveExecStatus extends Envelope {
  enabled?: boolean;
  configured?: boolean;
  locked?: boolean; // Owner Settings master lock — true = all live functions gated (default)
  armed?: boolean;
  armedExpiresAt?: string | null;
  keysFromVault?: boolean;
  caps?: {
    maxOrderNotional?: number; maxOrdersPerDay?: number; maxDailyNotional?: number;
    armTtlMinutes?: number; requireBracket?: boolean; maxDailyLossPct?: number; maxPriceDriftPct?: number;
  };
  today?: { day?: string; orders?: number; notional?: number; ordersRemaining?: number; notionalRemaining?: number };
}
// Owner enters passphrase + a FRESH per-order 2FA; the server holds every gate.
export interface LiveArmInput { passphrase: string; code: string; }
export interface LiveExecInput {
  symbol: string; side: 'buy' | 'sell'; qty: number; limitPrice: number;
  stopPrice?: number; takeProfitPrice?: number; code: string;
}
export interface LiveExecResult extends Envelope {
  live?: boolean; armed?: boolean; armedExpiresAt?: string | null;
  order?: { id?: string; symbol?: string; side?: string; qty?: string | number; status?: string; type?: string; submittedAt?: string };
}
// STOP-LIVE kill switch result: reduce-only (cancel all + flatten all + disarm + lock).
export interface LiveKillResult extends Envelope {
  disarmed?: boolean; locked?: boolean; brokerOk?: boolean; note?: string;
  canceled?: { ok?: boolean; count?: number; error?: string } | null;
  flattened?: { ok?: boolean; count?: number; error?: string } | null;
}

export interface Envelope {
  ok?: boolean;
  error?: string;
  [extra: string]: unknown;
}

// ---- Oculus-Clipeus auth types (native gate: components/AuthGate.tsx) ----
export interface OrdoTerms {
  version?: string;
  effectiveDate?: string;
  title?: string;
  body?: string;
  acknowledgments?: string[];
  draft?: boolean;
  owner?: string;
  governingLaw?: string;
  references?: Array<{ label?: string; url?: string }>;
}
export interface LoginResult {
  role?: string;
  mustChangePassword: boolean;
}
export interface AuthError extends Error {
  pending?: boolean;
  termsChanged?: boolean;
  currentTermsVersion?: string;
  twoFactorRequired?: boolean;
}
export interface ProfileInput {
  fullName: string;
  birthday: string;   // YYYY-MM-DD
  email: string;
}
export interface AdminUser {
  username: string;
  role: string;
  status: 'active' | 'pending' | 'revoked' | string;
  mustChangePassword?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface EventChain {
  target_asset?: string;
  direction?: '+' | '-' | string;
  mechanism?: string;
  features?: string[];
  confidence?: string;
  priced_in_risk?: string;
  expected_lag?: string;
  caveat?: string;
}

export interface EventSignal {
  event?: string;
  feedSource?: string;
  observedAt?: string;
  degraded?: boolean;
  detection?: { signal_type?: string };
  chains?: EventChain[];
}

export interface CoMovementRow {
  lag?: number;
  samples?: number;
  avgForwardMovePct?: number;
  directionalHitRate?: number | null;
}

// ---- Alpaca broker (READ-ONLY account view + PAPER-ONLY orders) ----
// These mirror the local DataHub's account.js / paper-broker.js shapes. The LIVE
// account is read-only (GET only, server-side); orders only ever target the Alpaca
// PAPER account. Account data + keys live on the local hub only (never the edge).
export interface AccountPosition {
  symbol?: string;
  qty?: number | null;
  avgEntry?: number | null;
  marketValue?: number | null;
  unrealizedPL?: number | null;
  side?: string;
}
export interface AccountOrder {
  id?: string | null;
  symbol?: string;
  side?: string;
  qty?: number | null;
  type?: string;
  status?: string;
  filledQty?: number | null;
  limitPrice?: number | null;
  filledAvgPrice?: number | null;
  submittedAt?: string;
}
export interface AccountState {
  ok?: boolean;
  env: 'live' | 'paper';
  mode: string; // 'live (read-only)' | 'paper'
  accountStatus: 'healthy' | 'mock' | 'unreachable' | 'unavailable' | string;
  equity?: number | null;
  lastEquity?: number | null;
  dayPnL?: number | null;
  dailyLoss?: number | null;
  buyingPower?: number | null;
  cash?: number | null;
  daytradeCount?: number | null;
  openPositions?: number | null;
  positionSymbols?: string[];
  tradesToday?: number | null;
  positions?: AccountPosition[];
  orders?: AccountOrder[];
  asOf?: string;
  error?: string;
}
export interface PaperOrderInput {
  symbol: string;
  side?: 'buy' | 'sell';
  qty: number;
  limitPrice?: number;
  type?: 'market' | 'limit'; // validated server-side against limitPrice
}

// READ-ONLY asset tradability metadata (GET /api/account/asset/:symbol — hub only).
export interface AccountAssetInfo extends Envelope {
  env?: string;
  accountStatus?: string;
  asset?: {
    symbol?: string; name?: string | null; exchange?: string | null; class?: string | null;
    status?: string | null; tradable?: boolean; marginable?: boolean; shortable?: boolean;
    easyToBorrow?: boolean; fractionable?: boolean;
  };
}
export interface PaperOrderResult {
  ok?: boolean;
  paper?: boolean;
  order?: { id?: string; symbol?: string; side?: string; qty?: string | number; status?: string; type?: string; submittedAt?: string };
  error?: string;
  symbol?: string;
}
export interface PaperExportResult {
  ok?: boolean;
  paper?: boolean;
  submitted: number;
  total: number;
  results: PaperOrderResult[];
  error?: string;
}
export interface BrokerKeysInput {
  alpacaPaperKeyId?: string;
  alpacaPaperSecret?: string;
  alpacaLiveKeyId?: string;
  alpacaLiveSecret?: string;
  alphaVantageKey?: string;
  finnhubKey?: string;
  krakenKey?: string;
  krakenSecret?: string;
  massiveKey?: string;
}

// REAL futures settlement (Massive free tier) — Futures Atlas + commodities autofill.
export interface FuturesQuote extends Envelope {
  root?: string;
  contract?: string;
  daysToMaturity?: number | null;
  settlement?: number;
  settlementDate?: string;
  closes?: number[];
  source?: string;
  stale?: boolean;
  pendingKey?: boolean;
  note?: string;
  asOf?: string;
}
export interface BrokerKeysResult {
  ok?: boolean;
  saved?: string[];
  connected?: { paper?: boolean; live?: boolean; alphaVantage?: boolean; finnhub?: boolean };
  error?: string;
}
// READ-ONLY broker key-PRESENCE report (GET /api/broker/status). Booleans only — never a key value.
export interface BrokerStatus extends Envelope {
  local?: boolean;
  keys?: { paper?: boolean; live?: boolean; kraken?: boolean };
}

// READ-ONLY Kraken balances (owner + loopback; query-only keys, no order path).
export interface KrakenBalance extends Envelope {
  broker?: string;
  readOnly?: boolean;
  balances?: { asset: string; amount: number }[];
  note?: string;
  asOf?: string;
}

// Durable paper account envelope (GET /api/paper/account — hub summarize()).
export interface PaperPrediction {
  id: string;
  symbol?: string;
  direction?: string;
  entryPrice?: number | null;
  exitPrice?: number | null;
  pnl?: number | null;
  outcome?: string;
  horizon?: string;
  source?: string;
  portfolioId?: string;
  openedAt?: string;
  closedAt?: string;
  thesis?: string;
  projectedVsOutcome?: Record<string, unknown>;
}
export interface PaperAccount extends Envelope {
  accountId?: string;
  mode?: string;
  broker?: string;
  startingCash?: number;
  cash?: number;
  equity?: number;
  realizedPnL?: number;
  portfolios?: { id: string; label?: string; startingCash?: number; equity?: number; realizedPnL?: number; openCount?: number; closedCount?: number; winRate?: number | null }[];
  openCount?: number;
  closedCount?: number;
  winRate?: number | null;
  wins?: number;
  losses?: number;
  flats?: number;
  openPredictions?: PaperPrediction[];
  recentClosed?: PaperPrediction[];
  asOf?: string;
  invariant?: string;
  skipped?: boolean;
}

// Durable paper prediction (local hub) — persists a forecast for Projected-vs-Outcome scoring.
export interface PredictionInput {
  source?: string;
  symbol: string;
  assetType?: string;
  direction: string;          // long/short/up/down (server normalizes)
  horizon?: string;
  entryPrice?: number;
  confidence?: number;        // 0..1
  thesis?: string;
  projected?: Record<string, unknown>;
  projectedVsOutcome?: Record<string, unknown>;
}
export interface PredictionResult { ok?: boolean; id?: string; skipped?: boolean; error?: string; prediction?: unknown; }

// Zero-knowledge broker vault (edge). `blob` is opaque ciphertext sealed in the browser.
export interface VaultStatus { ok?: boolean; hasPin: boolean; vaultSalt: string | null; accountCount: number; error?: string; }
export interface VaultAccount { id: string; label: string; broker: string; mode: string; createdAt: string; updatedAt: string; }
export interface VaultAccountList { ok?: boolean; accounts: VaultAccount[]; error?: string; }
export interface VaultAccountBlob { ok?: boolean; account: VaultAccount; blob: unknown; error?: string; }
export interface VaultPinResult { ok?: boolean; vaultSalt: string; created: boolean; error?: string; }
export interface VaultAddInput { pin: string; label: string; broker: string; mode: string; blob: unknown; }
export interface VaultAddResult { ok?: boolean; account: VaultAccount; error?: string; }

function resolveDefaultBase(): string {
  const origin = (typeof location !== 'undefined' && location.origin) || '';
  const onLocalOrigin = LOCAL_HOST_RE.test(origin);
  const port = typeof location !== 'undefined' && location.port ? Number(location.port) : null;
  const onDesktopWrapped = onLocalOrigin && port !== null && port >= 8787 && port <= 8797;
  if (onDesktopWrapped) return origin; // Electron: suite + datahub share one origin
  return onLocalOrigin ? LOCAL_DEFAULT : EDGE_DEFAULT;
}

function resolveBase(): string {
  const fallback = resolveDefaultBase();
  try {
    const saved = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
    if (saved) {
      const onHosted = !LOCAL_HOST_RE.test((typeof location !== 'undefined' && location.origin) || '');
      // On a hosted page, ignore a stale saved localhost base (a leftover dev
      // "Connect") so we never fetch a server that isn't there.
      if (onHosted && LOCAL_HOST_RE.test(saved)) return fallback;
      return saved.replace(/\/+$/, '');
    }
  } catch {
    /* no storage */
  }
  return fallback;
}

export function isLocalDataHubUrl(url: string): boolean {
  return LOCAL_HOST_RE.test(url) || /^https?:\/\/\[::1\](:\d+)?$/i.test(url);
}

export class DataHubClient {
  baseUrl: string;

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl ?? resolveBase();
  }

  getDataHubUrl(): string {
    return this.baseUrl;
  }

  isLocal(): boolean {
    return isLocalDataHubUrl(this.baseUrl);
  }

  private token(): string {
    try {
      return (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(AUTH_TOKEN_KEY)) || '';
    } catch {
      return '';
    }
  }

  private headers(extra?: Record<string, string>): Record<string, string> {
    const h: Record<string, string> = { Accept: 'application/json', ...(extra ?? {}) };
    const t = this.token();
    if (t) h.Authorization = `Bearer ${t}`;
    return h;
  }

  // Open the hub's SSE candle stream (desktop enhancement — the edge has no
  // stream route, and streaming is gated to the LOCAL hub by the caller).
  // Uses an authed fetch + reader instead of EventSource, which cannot send
  // the Authorization header. Returns a stop() that aborts the stream.
  openCandleStream(asset: string, symbol: string, onBar: (bar: Candle) => void, onState?: (open: boolean) => void): () => void {
    if (!this.isLocal()) return () => {};
    const ctl = new AbortController();
    const url = `${this.baseUrl}/api/stream/candles?asset=${encodeURIComponent(asset)}&symbol=${encodeURIComponent(symbol)}`;
    (async () => {
      try {
        const res = await fetch(url, { headers: this.headers(), signal: ctl.signal, cache: 'no-store' });
        if (!res.ok || !res.body) { onState?.(false); return; }
        const reader = res.body.getReader();
        const dec = new TextDecoder();
        let buf = '';
        let announced = false;
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += dec.decode(value, { stream: true });
          let idx: number;
          while ((idx = buf.indexOf('\n\n')) >= 0) {
            const chunk = buf.slice(0, idx);
            buf = buf.slice(idx + 2);
            const dataLine = chunk.split('\n').find((l) => l.startsWith('data: '));
            if (!dataLine || chunk.startsWith('event: hello')) continue;
            try {
              const bar = JSON.parse(dataLine.slice(6)) as Candle;
              if (!announced) { announced = true; onState?.(true); }
              if (Number.isFinite(bar.c)) onBar(bar);
            } catch { /* partial/heartbeat frame — skip */ }
          }
        }
      } catch { /* aborted or hub gone — the 30s poll keeps the chart honest */ }
      onState?.(false);
    })();
    return () => ctl.abort();
  }

  async getJson<T = Envelope>(path: string): Promise<T> {
    let res: Response;
    try {
      res = await fetch(this.baseUrl + path, {
        method: 'GET',
        cache: 'no-store',
        mode: 'cors',
        headers: this.headers(),
      });
    } catch (err) {
      throw new Error(`DataHub unreachable at ${this.baseUrl} (${(err as Error).message})`);
    }
    const body = (await res.json().catch(() => null)) as T | null;
    if (!res.ok) {
      const msg = (body as Envelope | null)?.error || `DataHub ${res.status}`;
      throw new Error(msg);
    }
    return (body ?? ({} as T)) as T;
  }

  async postJson<T = Envelope>(path: string, payload: unknown): Promise<T> {
    let res: Response;
    try {
      res = await fetch(this.baseUrl + path, {
        method: 'POST',
        cache: 'no-store',
        mode: 'cors',
        headers: this.headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload ?? {}),
      });
    } catch (err) {
      throw new Error(`DataHub unreachable at ${this.baseUrl} (${(err as Error).message})`);
    }
    const body = (await res.json().catch(() => null)) as T | null;
    if (!res.ok) {
      const msg = (body as Envelope | null)?.error || `DataHub ${res.status}`;
      throw new Error(msg);
    }
    return (body ?? ({} as T)) as T;
  }

  // ---- data methods (mirror datahub-client.js) ----
  history(ticker: string, days?: number): Promise<HistoryResponse> {
    const q = days ? `?days=${encodeURIComponent(days)}` : '';
    return this.getJson<HistoryResponse>(`/api/history/${encodeURIComponent(ticker.trim().toUpperCase())}${q}`);
  }

  // A pages-only deploy can run against a stale worker that predates the
  // asset-class history routes (404 on /api/history/{crypto|forex|commodities}).
  // Mirror engineUnavailable: resolve to an honest typed unavailable envelope
  // instead of throwing, so the calculators can render a clear "history
  // temporarily unavailable" state. Real failures (hub unreachable, 5xx,
  // provider errors) still reject so callers can tell them apart.
  async historyForAsset(asset: AssetClass, symbol: string, days?: number, contract?: string): Promise<HistoryResponse> {
    try {
      return await this.getJson<HistoryResponse>(historyPathForAsset(asset, symbol, days, contract));
    } catch (err) {
      if (!isHistoryRouteMissing(err)) throw err;
      return {
        ok: false,
        symbol: symbol.trim().toUpperCase(),
        error: `This DataHub deployment does not serve ${asset} history yet (route missing).`,
        note: 'History temporarily unavailable from this DataHub deployment.',
      };
    }
  }

  // OHLC candles for the candlestick chart. tf: '1D' | '7D' | '30D' | '12M'.
  // asset: 'stock' (Alpaca bars) | 'forex' (Alpha Vantage FX). Defaults to stock.
  candles(symbol: string, tf: string, asset = 'stock'): Promise<CandlesResponse> {
    const s = encodeURIComponent(symbol.trim().toUpperCase());
    return this.getJson<CandlesResponse>(`/api/candles/${s}?tf=${encodeURIComponent(tf)}&asset=${encodeURIComponent(asset)}`);
  }

  // ---- pattern engine cockpit (local hub only) ----
  // The engine writes leaderboard/signals/portfolio JSON on the user's machine;
  // only the local DataHub serves them. On the hosted edge there is no engine
  // output, so resolve to a clear "desktop only" envelope WITHOUT a network call
  // (mirrors accountState / caesar* guards) — never a 404/CORS error on the site.
  private engineUnavailable<T extends Envelope>(extra: T): T {
    return { ok: false, practice: true, error: 'The pattern engine runs in the desktop app (local DataHub) only.', ...extra };
  }
  engineLeaderboard(): Promise<EngineLeaderboard> {
    if (!this.isLocal()) return Promise.resolve(this.engineUnavailable<EngineLeaderboard>({ entries: [] }));
    return this.getJson<EngineLeaderboard>('/api/engine/leaderboard');
  }
  engineSignals(): Promise<EngineSignals> {
    if (!this.isLocal()) return Promise.resolve(this.engineUnavailable<EngineSignals>({ signals: [] }));
    return this.getJson<EngineSignals>('/api/engine/signals');
  }
  enginePortfolio(): Promise<EnginePortfolio> {
    if (!this.isLocal()) return Promise.resolve(this.engineUnavailable<EnginePortfolio>({ openPositions: [], recentClosed: [] }));
    return this.getJson<EnginePortfolio>('/api/engine/portfolio');
  }
  engineRisk(): Promise<EngineRisk> {
    if (!this.isLocal()) return Promise.resolve(this.engineUnavailable<EngineRisk>({ clusters: [] }));
    return this.getJson<EngineRisk>('/api/engine/risk');
  }
  engineRegime(): Promise<EngineRegime> {
    if (!this.isLocal()) return Promise.resolve(this.engineUnavailable<EngineRegime>({ byTicker: {} }));
    return this.getJson<EngineRegime>('/api/engine/regime');
  }
  engineJournal(): Promise<EngineJournal> {
    if (!this.isLocal()) return Promise.resolve(this.engineUnavailable<EngineJournal>({ recent: [] }));
    return this.getJson<EngineJournal>('/api/engine/journal');
  }
  engineCalendar(): Promise<EngineCalendar> {
    if (!this.isLocal()) return Promise.resolve(this.engineUnavailable<EngineCalendar>({ flags: [] }));
    return this.getJson<EngineCalendar>('/api/engine/calendar');
  }
  engineOptions(): Promise<EngineOptionsAnalytics> {
    if (!this.isLocal()) return Promise.resolve(this.engineUnavailable<EngineOptionsAnalytics>({ tickers: [] }));
    return this.getJson<EngineOptionsAnalytics>('/api/engine/options');
  }
  engineExecutionGap(): Promise<EngineExecutionGap> {
    if (!this.isLocal()) return Promise.resolve(this.engineUnavailable<EngineExecutionGap>({ orders: [] }));
    return this.getJson<EngineExecutionGap>('/api/engine/execution-gap');
  }
  engineFusionPooled(): Promise<EngineFusionPooled> {
    if (!this.isLocal()) return Promise.resolve(this.engineUnavailable<EngineFusionPooled>({ study: [] }));
    return this.getJson<EngineFusionPooled>('/api/engine/fusion-pooled');
  }
  engineBrain(): Promise<EngineBrain> {
    if (!this.isLocal()) return Promise.resolve(this.engineUnavailable<EngineBrain>({ decisions: [] }));
    return this.getJson<EngineBrain>('/api/engine/brain');
  }
  enginePerformance(): Promise<EnginePerformance> {
    if (!this.isLocal()) return Promise.resolve(this.engineUnavailable<EnginePerformance>({}));
    return this.getJson<EnginePerformance>('/api/engine/performance');
  }
  engineRealRisk(): Promise<EngineRealRisk> {
    if (!this.isLocal()) return Promise.resolve(this.engineUnavailable<EngineRealRisk>({}));
    return this.getJson<EngineRealRisk>('/api/engine/real-risk');
  }

  // Keyless yfinance second-opinion analyzer (runs Python on the local machine).
  // Local hub only; on the edge there's no Python, so degrade cleanly.
  research(ticker: string): Promise<ResearchReport> {
    const t = String(ticker || '').trim().toUpperCase();
    if (!t) return Promise.resolve({ ok: false, error: 'Enter a ticker symbol.' });
    if (!this.isLocal())
      return Promise.resolve({ ok: false, error: 'The research analyzer runs in the desktop app (local DataHub) only.' });
    return this.getJson<ResearchReport>(`/api/research/${encodeURIComponent(t)}`);
  }

  topAsset(asset: string, top = 10): Promise<Envelope & { coins?: TopAssetItem[]; list?: TopAssetItem[] }> {
    const a = String(asset || '').trim().toLowerCase().replace(/[^a-z]/g, '');
    if (!a) throw new Error('Pick an asset class for the Top list.');
    return this.getJson(`/api/top/${encodeURIComponent(a)}?top=${encodeURIComponent(Math.max(1, Math.min(top, 50)))}`);
  }

  autofillOptions(ticker: string, daysToExpiry?: number): Promise<Envelope> {
    const q = daysToExpiry ? `?daysToExpiry=${encodeURIComponent(daysToExpiry)}` : '';
    return this.getJson(`/api/autofill/options/${encodeURIComponent(ticker.trim().toUpperCase())}${q}`);
  }

  autofillDayTrade(ticker: string): Promise<Envelope> {
    return this.getJson(`/api/autofill/daytrade/${encodeURIComponent(ticker.trim().toUpperCase())}`);
  }

  autofillCrypto(symbol: string): Promise<Envelope> {
    return this.getJson(`/api/autofill/crypto/${encodeURIComponent(symbol.trim().toUpperCase())}`);
  }

  autofillCommodity(symbol: string, contract?: string): Promise<Envelope> {
    const q = contract ? `?contract=${encodeURIComponent(contract.trim().toUpperCase())}` : '';
    return this.getJson(`/api/autofill/commodities/${encodeURIComponent(symbol.trim().toUpperCase())}${q}`);
  }

  autofillForex(pair: string, accountCurrency = 'USD'): Promise<Envelope> {
    const q = `?accountCurrency=${encodeURIComponent(accountCurrency.trim().toUpperCase())}`;
    return this.getJson(`/api/autofill/forex/${encodeURIComponent(pair.trim().toUpperCase())}${q}`);
  }

  eventSignalFeed(since?: string): Promise<Envelope & { signals?: EventSignal[]; analysis?: CoMovementRow[]; generatedAt?: string }> {
    const q = since ? `?since=${encodeURIComponent(since)}` : '';
    return this.getJson(`/api/event-signal/feed${q}`);
  }

  // ---- live status (shared market strip / home status block) ----
  health(): Promise<Envelope & { service?: string; provider?: string }> {
    return this.getJson('/health');
  }

  authStatus(): Promise<
    Envelope & { authenticated?: boolean; role?: string; authEnabled?: boolean; totpRequired?: boolean; pinMode?: boolean; multiUser?: boolean; mustChangePassword?: boolean; termsVersion?: string; twoFactor?: { enabled?: boolean; pending?: boolean } | null }
  > {
    return this.getJson('/api/auth/status');
  }

  // ---- Oculus-Clipeus auth (native gate: components/AuthGate.tsx) ----
  hasToken(): boolean {
    return Boolean(this.token());
  }

  setToken(token: string): void {
    try {
      if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(AUTH_TOKEN_KEY, token);
    } catch {
      /* no storage */
    }
  }

  clearToken(): void {
    try {
      if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(AUTH_TOKEN_KEY);
    } catch {
      /* no storage */
    }
  }

  logout(): void {
    this.clearToken();
  }

  // Low-level POST that returns status + parsed body — unlike postJson, which collapses a
  // non-2xx into a flat Error. The gate needs the status/flags to route a pending member
  // (403 { pending:true }) and a terms-version drift (409) instead of just showing a string.
  private async authPost(
    path: string,
    payload: unknown,
  ): Promise<{ status: number; body: (Envelope & Record<string, unknown>) | null }> {
    let res: Response;
    try {
      res = await fetch(this.baseUrl + path, {
        method: 'POST',
        cache: 'no-store',
        mode: 'cors',
        headers: this.headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload ?? {}),
      });
    } catch (err) {
      throw new Error(`DataHub unreachable at ${this.baseUrl} (${(err as Error).message})`);
    }
    const body = (await res.json().catch(() => null)) as (Envelope & Record<string, unknown>) | null;
    return { status: res.status, body };
  }

  // Owner login is passphrase-only (the edge verifies the password against the owner hash
  // regardless of username); members supply a username + optional 6-digit TOTP when
  // totpRequired. Stores the session token on success. Throws an AuthError with `.pending`
  // for a member awaiting owner approval (403).
  async login(password: string, username?: string, code?: string): Promise<LoginResult> {
    const payload: Record<string, string> = { password: String(password ?? '') };
    if (username && username.trim()) payload.username = username.trim().toLowerCase();
    if (code && code.trim()) payload.code = code.trim();
    const { status, body } = await this.authPost('/api/auth/login', payload);
    if (status === 200 && body?.ok && typeof body.token === 'string') {
      this.setToken(body.token);
      return { role: body.role as string | undefined, mustChangePassword: Boolean(body.mustChangePassword) };
    }
    if (status === 403 && body?.pending) {
      const e = new Error((body.error as string) || 'Account pending owner approval.') as AuthError;
      e.pending = true;
      throw e;
    }
    if (body?.twoFactorRequired) {
      const e = new Error((body.error as string) || 'Enter your 6-digit authenticator code.') as AuthError;
      e.twoFactorRequired = true;
      throw e;
    }
    throw new Error((body?.error as string) || `Sign-in failed (${status}).`);
  }

  // ---- per-user login 2FA (authenticator-app TOTP) management ----
  twoFactorSetup(): Promise<{ ok?: boolean; secret: string; otpauthUri: string; error?: string }> {
    return this.postJson('/api/auth/2fa/setup', {});
  }
  twoFactorEnable(code: string): Promise<{ ok?: boolean; enabled: boolean; error?: string }> {
    return this.postJson('/api/auth/2fa/enable', { code });
  }
  twoFactorDisable(code: string): Promise<{ ok?: boolean; enabled: boolean; error?: string }> {
    return this.postJson('/api/auth/2fa/disable', { code });
  }

  // Self-serve signup -> pending owner approval. `accepted` + `termsVersion` gate the create
  // server-side; a 409 means the Terms version drifted and the gate must reload + re-accept.
  async register(username: string, password: string, termsVersion: string, profile: ProfileInput): Promise<void> {
    const { status, body } = await this.authPost('/api/auth/register', {
      username,
      password,
      accepted: true,
      termsVersion,
      profile,
    });
    if ((status === 201 || status === 200) && body?.ok) return;
    if (status === 409) {
      const e = new Error((body?.error as string) || 'Terms changed — reload and accept again.') as AuthError;
      e.termsChanged = true;
      e.currentTermsVersion = body?.currentTermsVersion as string | undefined;
      throw e;
    }
    throw new Error((body?.error as string) || `Sign-up failed (${status}).`);
  }

  getTerms(): Promise<Envelope & { terms?: OrdoTerms }> {
    return this.getJson('/api/auth/terms');
  }

  // Forced first-login (or voluntary) password change. Uses the must-change token already
  // stored by login() as Bearer; mints + stores a new full-scope token on success.
  async changePassword(currentPassword: string, newPassword: string): Promise<LoginResult> {
    const { status, body } = await this.authPost('/api/auth/change-password', { currentPassword, newPassword });
    if (status === 200 && body?.ok && typeof body.token === 'string') {
      this.setToken(body.token);
      return { role: body.role as string | undefined, mustChangePassword: false };
    }
    throw new Error((body?.error as string) || `Password change failed (${status}).`);
  }

  // ---- owner-only account administration (edge worker; the stored owner token is sent as
  // Bearer by headers(). Non-owner/expired tokens get 403/401 -> postJson throws the message) ----
  listUsers(): Promise<Envelope & { users?: AdminUser[] }> {
    return this.getJson('/api/auth/admin/list');
  }
  approveUser(username: string): Promise<Envelope> {
    return this.postJson('/api/auth/admin/approve', { username });
  }
  revokeUser(username: string): Promise<Envelope> {
    return this.postJson('/api/auth/admin/revoke', { username });
  }
  reinstateUser(username: string): Promise<Envelope> {
    return this.postJson('/api/auth/admin/reinstate', { username });
  }
  // Owner provisions a member with a temp password (lands active, must-change-password).
  createUser(username: string, tempPassword: string, role: 'member' | 'owner' = 'member'): Promise<Envelope> {
    return this.postJson('/api/auth/admin/create', { username, tempPassword, role });
  }
  // Owner resets a member's password to a new temp and re-arms must-change-password.
  resetUserPassword(username: string, tempPassword: string): Promise<Envelope> {
    return this.postJson('/api/auth/admin/reset', { username, tempPassword });
  }

  marketClock(): Promise<
    Envelope & { isOpen?: boolean; nextOpen?: string; nextClose?: string; source?: string }
  > {
    return this.getJson('/api/market-clock');
  }

  // Caesar (formerly To'mas) paths are local-DataHub only; on the edge they resolve to a
  // clear local_only envelope without a network call (mirrors the legacy gate).
  // Canonical route is /api/caesar/* (the legacy /api/tomas/* stays mounted
  // permanently server-side); responses carry BOTH the canonical `caesar` key
  // and the legacy `tomas` key, so callers can read either.
  caesarChat(message: string): Promise<Envelope & {
    tomas?: { status?: string; reply?: string; provider?: string; model?: string };
    caesar?: { status?: string; reply?: string; provider?: string; model?: string };
  }> {
    // On the website, Caesar chat runs on the edge worker but is LOGIN-GATED.
    // Without a session token there is nothing to send (the worker returns 401),
    // so surface a clear prompt instead of a failed request. Logged-in calls carry
    // the Bearer token via headers() and hit the worker's /api/caesar/chat.
    // Desktop (isLocal) always calls the local hub regardless of token.
    if (!this.isLocal() && !this.token()) {
      const gated = { enabled: false, provider: 'edge', status: 'login_required', reply: 'Log in to use Caesar chat on the website.' };
      return Promise.resolve({ ok: true, tomas: gated, caesar: gated });
    }
    return this.postJson('/api/caesar/chat', { message: String(message ?? '') });
  }

  /** @deprecated Use caesarChat — thin back-compat alias, will be removed after the rename settles. */
  tomasChat(message: string): ReturnType<DataHubClient['caesarChat']> {
    return this.caesarChat(message);
  }

  caesarReview(evidence: unknown): Promise<Envelope & {
    tomas?: { status?: string; review?: unknown };
    caesar?: { status?: string; review?: unknown };
  }> {
    if (!this.isLocal()) {
      const localOnly = { enabled: false, provider: 'client', status: 'local_only', review: null };
      return Promise.resolve({ ok: true, tomas: localOnly, caesar: localOnly });
    }
    return this.postJson('/api/caesar/review', { evidence: evidence ?? {} });
  }

  /** @deprecated Use caesarReview — thin back-compat alias, will be removed after the rename settles. */
  tomasReview(evidence: unknown): ReturnType<DataHubClient['caesarReview']> {
    return this.caesarReview(evidence);
  }

  // ---- Alpaca broker (local DataHub / Caesar only — account data + keys never live on the edge) ----

  // READ-ONLY account snapshot. env='live' reads the REAL account (server does GET-only calls);
  // 'paper' reads the paper account. On the hosted edge there is no account data and no broker
  // route, so resolve to a clear 'unavailable' snapshot WITHOUT a network call (mirrors the
  // caesar* local_only guard) — never a 404/CORS error on the live site.
  accountState(env: 'live' | 'paper' = 'paper'): Promise<AccountState> {
    const e: 'live' | 'paper' = env === 'live' ? 'live' : 'paper';
    if (!this.isLocal()) {
      return Promise.resolve({
        ok: true,
        env: e,
        mode: e === 'live' ? 'live (read-only)' : 'paper',
        accountStatus: 'unavailable',
        equity: null, lastEquity: null, dayPnL: null, dailyLoss: null,
        buyingPower: null, cash: null, daytradeCount: null,
        openPositions: null, positionSymbols: [], tradesToday: null,
        positions: [], orders: [],
        asOf: new Date().toISOString(),
        error: 'Broker view is available in the desktop app (local DataHub) only.',
      });
    }
    return this.getJson<AccountState>(`/api/account/state?env=${encodeURIComponent(e)}`);
  }

  // READ-ONLY asset tradability metadata (tradable / shortable / fractionable) for
  // pre-trade INFORMATION on the paper ticket. Hub-only, GET-only.
  accountAsset(symbol: string, env: 'live' | 'paper' = 'paper'): Promise<AccountAssetInfo> {
    const s = String(symbol || '').trim().toUpperCase();
    if (!s) return Promise.resolve({ ok: false, error: 'Enter a symbol.' });
    if (!this.isLocal()) return Promise.resolve({ ok: false, error: 'local hub only' });
    return this.getJson<AccountAssetInfo>(`/api/account/asset/${encodeURIComponent(s)}?env=${env === 'live' ? 'live' : 'paper'}`)
      .catch(() => ({ ok: false } as AccountAssetInfo));
  }

  // READ-ONLY. Owner-only live-execution STATUS (armed / locked) — a GET of
  // state, never an action. Owner+loopback gated server-side; off the local hub
  // (or when the session isn't the owner) this resolves to ok:false and the UI
  // simply hides the indicator. Arming itself stays passphrase+2FA in its own flow.
  liveExecStatus(): Promise<LiveExecStatus> {
    if (!this.isLocal()) return Promise.resolve({ ok: false, error: 'local hub only' });
    return this.getJson<LiveExecStatus>('/api/live/status').catch(() => ({ ok: false } as LiveExecStatus));
  }

  // ── OWNER-ONLY REAL-MONEY execution (all server-gated: owner + loopback +
  // capability + passphrase + per-order 2FA + caps + mandatory bracket). These
  // are the ONLY methods in the client that can move real money, and only after
  // the owner explicitly enabled LIVE_EXECUTION_ENABLED and armed it. Local-hub
  // only. The owner types their own passphrase + 2FA; values pass straight
  // through to the loopback hub. ──
  liveArm(input: LiveArmInput): Promise<LiveExecStatus> {
    if (!this.isLocal()) return Promise.resolve({ ok: false, error: 'local hub only' });
    return this.postJson<LiveExecStatus>('/api/live/arm', input);
  }
  liveDisarm(): Promise<LiveExecStatus> {
    if (!this.isLocal()) return Promise.resolve({ ok: false, error: 'local hub only' });
    return this.postJson<LiveExecStatus>('/api/live/disarm', {});
  }
  // Owner Settings master lock. UNLOCK needs the owner passphrase + a fresh 2FA,
  // verified SERVER-SIDE (same primitives as arm). LOCK is always allowed. Local-hub only.
  liveUiUnlock(input: LiveArmInput): Promise<LiveExecStatus> {
    if (!this.isLocal()) return Promise.resolve({ ok: false, error: 'local hub only' });
    return this.postJson<LiveExecStatus>('/api/live/ui-unlock', input);
  }
  liveUiLock(): Promise<LiveExecStatus> {
    if (!this.isLocal()) return Promise.resolve({ ok: false, error: 'local hub only' });
    return this.postJson<LiveExecStatus>('/api/live/ui-lock', {});
  }
  liveExecute(input: LiveExecInput): Promise<LiveExecResult> {
    if (!this.isLocal()) return Promise.resolve({ ok: false, error: 'local hub only' });
    return this.postJson<LiveExecResult>('/api/live/execute', input);
  }
  // STOP-LIVE kill switch: reduce-only emergency de-risk (cancel all live orders +
  // flatten all positions + disarm + lock). NO passphrase/2FA — usable anytime.
  // Local-hub only, like the other live methods.
  liveKillSwitch(): Promise<LiveKillResult> {
    if (!this.isLocal()) return Promise.resolve({ ok: false, error: 'local hub only' } as LiveKillResult);
    return this.postJson<LiveKillResult>('/api/live/stop', {});
  }

  // PAPER ONLY. Places ONE simulated order on the Alpaca PAPER account via the local DataHub's
  // paper-broker (hardcoded to the paper host, clamped to the Caesar caps). There is no live
  // order path anywhere. Local-only: off the hub there is no broker route, so refuse clearly.
  placePaperOrder(order: PaperOrderInput): Promise<PaperOrderResult> {
    if (!this.isLocal()) {
      return Promise.resolve({ ok: false, paper: true, error: 'Paper trading runs on the local DataHub (desktop) only.' });
    }
    return this.postJson<PaperOrderResult>('/api/broker/paper/order', order);
  }

  // PAPER ONLY. Submits a batch of paper-log rows to the Alpaca PAPER account as paper orders.
  exportPaperOrders(orders: PaperOrderInput[]): Promise<PaperExportResult> {
    if (!this.isLocal()) {
      return Promise.resolve({ ok: false, paper: true, submitted: 0, total: orders.length, results: [], error: 'Paper export runs on the local DataHub (desktop) only.' });
    }
    return this.postJson<PaperExportResult>('/api/broker/paper/export', { orders });
  }

  // Persist a saved forecast as a durable paper PREDICTION on the local hub, so Projected-vs-
  // Outcome survives beyond the browser ledger. Local-only: the edge has no predictions endpoint,
  // so off the hub this is a graceful no-op (the row still lives in the browser ledger).
  savePrediction(input: PredictionInput): Promise<PredictionResult> {
    if (!this.isLocal()) {
      return Promise.resolve({ ok: false, skipped: true, error: 'Durable forecast storage runs on the local DataHub (desktop).' });
    }
    return this.postJson<PredictionResult>('/api/paper/predictions', input);
  }

  // Durable paper account (GET /api/paper/account) — the hub's simulation ledger
  // is the SOURCE OF TRUTH for the Paper Portfolio page; the browser ledger is a
  // labeled offline fallback. Local-only: the edge has no paper-account routes.
  paperAccount(): Promise<PaperAccount> {
    if (!this.isLocal()) return Promise.resolve({ ok: false, skipped: true, error: 'Durable paper account lives on the local DataHub (desktop).' } as PaperAccount);
    return this.getJson<PaperAccount>('/api/paper/account').catch((e) => ({ ok: false, error: String((e as Error)?.message || 'Unavailable') } as PaperAccount));
  }

  // Reset the durable paper account (owner action; POST behind the /api gate).
  resetPaperAccount(startingCash?: number): Promise<PaperAccount> {
    if (!this.isLocal()) return Promise.resolve({ ok: false, skipped: true, error: 'Local DataHub only.' } as PaperAccount);
    return this.postJson<PaperAccount>('/api/paper/reset', startingCash ? { startingCash } : {});
  }

  // Close an open durable prediction (exit price -> hub computes pnl/outcome +
  // the Projected-vs-Outcome comparison).
  closePaperPrediction(id: string, body: { exitPrice?: number; pnl?: number; notes?: string }): Promise<{ ok: boolean; prediction?: PaperPrediction; account?: PaperAccount; error?: string }> {
    if (!this.isLocal()) return Promise.resolve({ ok: false, error: 'Local DataHub only.' });
    return this.postJson(`/api/paper/predictions/${encodeURIComponent(id)}/close`, body);
  }

  // Link a broker: send the user's API keys to the LOCAL hub, which stores them on the machine
  // (owner + loopback only) and reports which accounts connected. Local-only — off the hub there
  // is no such route, so refuse clearly. Key VALUES go straight to the local server, never to the
  // edge and never anywhere this client persists them.
  saveBrokerKeys(input: BrokerKeysInput): Promise<BrokerKeysResult> {
    if (!this.isLocal()) {
      return Promise.resolve({ ok: false, error: 'Linking a broker runs on the local DataHub (desktop) only.' });
    }
    return this.postJson<BrokerKeysResult>('/api/broker/keys', input);
  }

  // READ-ONLY broker key-PRESENCE (booleans only, never values). Lets each per-page execute panel
  // default to "Pending Broker" when the path-appropriate keys aren't on the local hub. Local-hub
  // only (mirrors accountAsset()/liveExecStatus() guards); off the hub there is no such route.
  brokerStatus(): Promise<BrokerStatus> {
    if (!this.isLocal()) return Promise.resolve({ ok: false, local: false, keys: { paper: false, live: false } });
    return this.getJson<BrokerStatus>('/api/broker/status').catch(() => ({ ok: false } as BrokerStatus));
  }

  // READ-ONLY Kraken balances (owner + loopback on the hub; query-only keys, no order path).
  brokerKrakenBalance(): Promise<KrakenBalance> {
    if (!this.isLocal()) return Promise.resolve({ ok: false, error: 'Local DataHub only.' } as KrakenBalance);
    return this.getJson<KrakenBalance>('/api/broker/kraken/balance').catch((e) => ({ ok: false, error: String((e as Error)?.message || 'Unavailable') } as KrakenBalance));
  }

  // REAL futures settlement for a CME product root (Massive free tier) — served
  // by the local hub on desktop AND by the edge worker on the hosted site.
  // pendingKey:true = no data key linked there yet — honest, not an error.
  futuresQuote(root: string): Promise<FuturesQuote> {
    return this.getJson<FuturesQuote>(`/api/futures/quote/${encodeURIComponent(root)}`).catch((e) => {
      const msg = String((e as Error)?.message || 'Unavailable');
      return { ok: false, pendingKey: /pending|No futures data key/i.test(msg) || undefined, error: msg } as FuturesQuote;
    });
  }

  // ---- zero-knowledge per-account broker vault (edge / website) ----
  // These carry ONLY opaque blobs sealed in the browser (apps/web/src/lib/broker-vault.ts):
  // the server never sees a password, a vault key, or a decrypted broker credential. The PIN
  // is an add/remove gate; the password (never sent) is the encryption secret.
  vaultStatus(): Promise<VaultStatus> { return this.getJson<VaultStatus>('/api/vault/status'); }
  vaultListAccounts(): Promise<VaultAccountList> { return this.getJson<VaultAccountList>('/api/vault/accounts'); }
  vaultGetAccount(id: string): Promise<VaultAccountBlob> { return this.getJson<VaultAccountBlob>(`/api/vault/accounts/${encodeURIComponent(id)}`); }
  vaultSetPin(input: { pin: string; currentPin?: string }): Promise<VaultPinResult> { return this.postJson<VaultPinResult>('/api/vault/pin', input); }
  vaultAddAccount(input: VaultAddInput): Promise<VaultAddResult> { return this.postJson<VaultAddResult>('/api/vault/accounts', input); }
  vaultRemoveAccount(input: { pin: string; id: string }): Promise<Envelope & { id?: string }> { return this.postJson('/api/vault/accounts/remove', input); }
}

// Shared singleton for the app.
export const dataHub = new DataHubClient();
